import cn from 'classnames';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Merge, MoreHorizontal, Plus, Scissors, Trash2 } from 'lucide-react';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useTranslation } from 'react-i18next';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

type Props = {
  segment: DocumentSubtitleSegmentV1;
  filteredItems: DocumentSubtitleSegmentV1[];
};

/** Same UI as the private caption editor's segment tools — two rounded
 *  buttons at the top (Plus + Split) plus a kebab menu that carries the
 *  remaining actions. The shared editor has no training-data flow, so
 *  the menu holds only "Merge with next" + "Remove". */
export const SharedCaptionSegmentEditingTools = ({
  segment,
  filteredItems
}: Props) => {
  const { addListItem, removeListItem, splitListItem, joinListItems } =
    useSharedCaption();
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const currentIndex = filteredItems.findIndex(item => item.id === segment.id);
  const hasNextSegment =
    currentIndex !== -1 && currentIndex < filteredItems.length - 1;

  const iconButton = cn(
    'inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors',
    'hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'cursor-pointer'
  );

  const menuItem = cn(
    'flex w-full items-center gap-2 px-3 py-1.5 text-xs cursor-pointer',
    'data-[focus]:opacity-80 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed'
  );

  const iconButtonStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('border'),
    color: getColour('icon')
  };

  return (
    <div className="flex flex-col items-end gap-0.5 p-0.5 pr-4">
      <Tooltip label={shortcutLabel(t('add'), 'Ctrl+Enter')} placement="left">
        <button
          type="button"
          onClick={() => addListItem(segment.id)}
          aria-label={t('add')}
          className={iconButton}
          style={iconButtonStyle}
        >
          <Plus size={13} aria-hidden="true" />
        </button>
      </Tooltip>

      <Tooltip
        label={shortcutLabel(t('split'), 'Ctrl+Shift+X')}
        placement="left"
      >
        <button
          type="button"
          onClick={() => splitListItem(segment.id)}
          aria-label={t('split')}
          className={iconButton}
          style={iconButtonStyle}
        >
          <Scissors size={13} aria-hidden="true" />
        </button>
      </Tooltip>

      <Menu as="div" className="relative">
        <Tooltip label={t('more-actions')} placement="left">
          <MenuButton
            aria-label={t('more-actions')}
            className={iconButton}
            style={iconButtonStyle}
          >
            <MoreHorizontal size={13} aria-hidden="true" />
          </MenuButton>
        </Tooltip>
        <MenuItems
          anchor="bottom end"
          className={cn(
            'min-w-52 rounded-2xl border shadow-lg overflow-hidden py-1 z-50',
            'focus:outline-none'
          )}
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border')
          }}
        >
          <MenuItem
            as="button"
            disabled={!hasNextSegment}
            onClick={() => joinListItems(segment.id)}
            className={menuItem}
            style={{ color: getColour('normalText') }}
          >
            <Merge size={13} aria-hidden="true" />
            <span>
              {hasNextSegment ? t('join-with-next') : t('no-next-segment')}
            </span>
          </MenuItem>
          <MenuItem
            as="button"
            onClick={() => removeListItem(segment.id)}
            className={menuItem}
            style={{ color: 'rgb(220 38 38)' }}
          >
            <Trash2 size={13} aria-hidden="true" />
            <span>{shortcutLabel(t('remove'), 'Ctrl+Shift+Backspace')}</span>
          </MenuItem>
        </MenuItems>
      </Menu>
    </div>
  );
};
